import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
    Users, UserPlus, RefreshCw, Search, Phone, Mail, MapPin,
    Clock, AlertTriangle, Shuffle, History, Filter, CheckSquare,
    ChevronRight, XCircle, ArrowRightLeft, Download, FileSpreadsheet, FileArchive, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsPoolPage = () => {
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [agents, setAgents] = useState([]);
    const [teams, setTeams] = useState([]);
    const [poolAgents, setPoolAgents] = useState([]); // agents who have rejected leads in the pool
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [teamFilter, setTeamFilter] = useState('all');
    const [agentFilter, setAgentFilter] = useState('all');
    const [crossTeamFilter, setCrossTeamFilter] = useState(false);
    const [selectedLeads, setSelectedLeads] = useState(new Set());
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [historyDetail, setHistoryDetail] = useState({ loading: false, items: [] });
    // Export state: indicates which generator is currently running so we can
    // show a spinner inside the dropdown trigger and disable double-clicks.
    const [exporting, setExporting] = useState(null); // 'leads_pool' | 'leads_pool_by_team' | null

    // Run the chosen admin export generator on the backend, then auto-download
    // the resulting file as soon as it's ready. Reuses the same authenticated
    // /api/admin/exports endpoints powering the Admin Exports page.
    const runExport = async (key, filename) => {
        if (exporting) return;
        setExporting(key);
        const tStart = toast.loading(
            key === 'leads_pool_by_team'
                ? 'Generating Excel (one file per team)…'
                : 'Generating Excel of the full pool…'
        );
        try {
            const genRes = await apiClient.post(`/admin/exports/generate/${key}`);
            const outFile = genRes?.data?.filename || filename;
            // Pull the file as a blob with the active auth token
            const token = sessionStorage.getItem('clt_token');
            const fileRes = await fetch(`${BACKEND_URL}/api/admin/exports/${encodeURIComponent(outFile)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!fileRes.ok) throw new Error(`Download failed (HTTP ${fileRes.status})`);
            const blob = await fileRes.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = outFile;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${outFile} (${genRes?.data?.size_human || ''})`, { id: tStart });
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Export failed';
            toast.error(msg, { id: tStart });
        } finally {
            setExporting(null);
        }
    };

    // Refetch leads whenever a server-side filter changes. The backend caches
    // the heavy aggregation for 30s so multiple filter switches in a row
    // are essentially free.
    useEffect(() => {
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamFilter, agentFilter, crossTeamFilter]);

    // One-shot: fetch filter dropdown options + assignable agents on mount.
    useEffect(() => {
        fetchSideData();
    }, []);

    const fetchSideData = async () => {
        try {
            const [usersRes, optsRes] = await Promise.all([
                // Include TLs + Sales Managers + MoAs alongside sales executives
                // so SM/CEO can assign pool leads to leaders (and themselves).
                apiClient.get('/users?roles=sales_executive,team_leader,sales_manager,master_of_academics'),
                apiClient.get('/leads/pool/filter-options').catch(() => ({ data: { teams: [], agents: [] } })),
            ]);
            // Order: sales_manager -> team_leader -> master_of_academics -> sales_executive
            const rolePriority = { sales_manager: 0, team_leader: 1, master_of_academics: 2, sales_executive: 3 };
            const activeUsers = usersRes.data
                .filter(u => u.is_active)
                .sort((a, b) => {
                    const ra = rolePriority[a.role] ?? 99;
                    const rb = rolePriority[b.role] ?? 99;
                    if (ra !== rb) return ra - rb;
                    return (a.full_name || '').localeCompare(b.full_name || '');
                });
            setAgents(activeUsers);
            setTeams(optsRes.data?.teams || []);
            setPoolAgents(optsRes.data?.agents || []);
        } catch (error) {
            // Non-fatal — dropdowns will be empty but the page still works.
        }
    };

    // Human-friendly role label shown next to each assignee in the dropdown
    const roleLabel = (r) => ({
        sales_manager: 'Sales Manager',
        team_leader: 'Team Leader',
        master_of_academics: 'Master of Academics',
        sales_executive: 'Sales Exec',
    }[r] || r);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const params = {};
            if (teamFilter && teamFilter !== 'all') params.team_filter = teamFilter;
            if (agentFilter && agentFilter !== 'all') params.agent_filter = agentFilter;
            if (crossTeamFilter) params.cross_team_only = true;
            const poolRes = await apiClient.get('/leads/pool', { params });
            setLeads(poolRes.data);
        } catch (error) {
            toast.error('Failed to fetch leads pool');
        } finally { setLoading(false); }
    };

    const fetchData = async () => {
        // Manual refresh button — pull both leads + filter options again.
        await Promise.all([fetchLeads(), fetchSideData()]);
    };

    const handleAssign = async (leadId, agentId = null) => {
        try {
            setAssigning(true);
            const url = agentId ? `/leads/pool/${leadId}/assign?user_id=${agentId}` : `/leads/pool/${leadId}/assign`;
            const res = await apiClient.post(url);
            toast.success(res.data.message);
            setShowAssignModal(false);
            setSelectedLead(null);
            setSelectedAgent('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to assign lead');
        } finally { setAssigning(false); }
    };

    const handleBulkAssign = async () => {
        if (!selectedAgent || selectedLeads.size === 0) return;
        try {
            setAssigning(true);
            const res = await apiClient.post('/leads/pool/bulk-assign', {
                lead_ids: [...selectedLeads],
                agent_id: selectedAgent,
            });
            toast.success(res.data.message);
            setShowBulkAssignModal(false);
            setSelectedLeads(new Set());
            setSelectedAgent('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to bulk assign');
        } finally { setAssigning(false); }
    };

    const toggleSelectLead = (leadId) => {
        setSelectedLeads(prev => {
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedLeads.size === filteredLeads.length) {
            setSelectedLeads(new Set());
        } else {
            setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const filteredLeads = useMemo(() => {
        // Team / agent / cross-team filters are now applied server-side; only
        // free-text search is handled here so typing stays snappy.
        if (!searchTerm) return leads;
        const s = searchTerm.toLowerCase();
        return leads.filter(l => l.full_name?.toLowerCase().includes(s) || l.phone?.includes(s) || l.email?.toLowerCase().includes(s));
    }, [leads, searchTerm]);

    // Filter dropdown agents now come from the backend's filter-options
    // endpoint (= unique agents who have rejected leads in the pool). Keep
    // the fallback for first render before that response arrives.
    const historyAgents = useMemo(() => poolAgents, [poolAgents]);

    const canManage = ['super_admin', 'admin', 'sales_manager', 'coo'].includes(user?.role);
    // All stat counts derive from the CURRENTLY FILTERED list (search +
    // server-side team/agent/cross-team) so the cards stay in sync with the
    // table beneath them.
    const totalCount = filteredLeads.length;
    const rejectedCount = filteredLeads.filter(l => l.stage === 'rejected' || l.stage === 'not_interested').length;
    const crossTeamCount = filteredLeads.filter(l => l.cross_team_eligible).length;
    const multiAssignedCount = filteredLeads.filter(l => (l.times_assigned || 0) > 1 || (l.rejection_count || 0) > 1).length;

    return (
        <div className="space-y-6" data-testid="leads-pool-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Leads Pool</h1>
                    <p className="text-muted-foreground">Rejected & unassigned leads — filter by team/agent to avoid re-assignment conflicts</p>
                </div>
                <div className="flex items-center gap-2">
                    {selectedLeads.size > 0 && canManage && (
                        <Button onClick={() => { setSelectedAgent(''); setShowBulkAssignModal(true); }} className="bg-primary" data-testid="bulk-assign-btn">
                            <CheckSquare className="h-4 w-4 mr-2" />Bulk Assign ({selectedLeads.size})
                        </Button>
                    )}
                    {canManage && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    disabled={!!exporting}
                                    data-testid="pool-export-btn"
                                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300"
                                >
                                    {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                    Export
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72">
                                <DropdownMenuLabel>Export Leads Pool</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => runExport('leads_pool', 'leads_pool_retargeting.xlsx')}
                                    className="flex items-start gap-3 cursor-pointer"
                                    data-testid="pool-export-all"
                                >
                                    <FileSpreadsheet className="h-5 w-5 text-emerald-600 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-sm">Full Pool (Excel)</p>
                                        <p className="text-xs text-muted-foreground">Single .xlsx with all pool leads + phone in E.164 for Meta upload</p>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => runExport('leads_pool_by_team', 'leads_pool_by_team.zip')}
                                    className="flex items-start gap-3 cursor-pointer"
                                    data-testid="pool-export-by-team"
                                >
                                    <FileArchive className="h-5 w-5 text-amber-600 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-sm">Split by Team (ZIP)</p>
                                        <p className="text-xs text-muted-foreground">One .xlsx per team in a single .zip</p>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    <Button onClick={fetchData} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
                </div>
            </div>

            {/* Stats — all counts reflect the currently active filters (team,
                agent, cross-team, search). "Available Agents" remains a global
                metric. */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total in Pool</p><p className="text-3xl font-bold" data-testid="pool-stat-total">{totalCount}</p></div><Users className="h-8 w-8 text-primary" /></div></CardContent></Card>
                <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Rejected</p><p className="text-3xl font-bold text-red-500" data-testid="pool-stat-rejected">{rejectedCount}</p></div><XCircle className="h-8 w-8 text-red-500" /></div></CardContent></Card>
                <Card className={crossTeamFilter ? 'ring-2 ring-orange-500' : ''} onClick={() => setCrossTeamFilter(!crossTeamFilter)} role="button">
                    <CardContent className="pt-6 cursor-pointer"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Cross-Team</p><p className="text-3xl font-bold text-orange-500" data-testid="pool-stat-crossteam">{crossTeamCount}</p><p className="text-xs text-muted-foreground">Rejected 2+ times</p></div><ArrowRightLeft className="h-8 w-8 text-orange-500" /></div></CardContent>
                </Card>
                <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Multi-Assigned</p><p className="text-3xl font-bold text-amber-500" data-testid="pool-stat-multi">{multiAssignedCount}</p><p className="text-xs text-muted-foreground">Assigned 2+ times</p></div><ArrowRightLeft className="h-8 w-8 text-amber-500" /></div></CardContent></Card>
                <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Available Agents</p><p className="text-3xl font-bold text-emerald-500">{agents.length}</p></div><UserPlus className="h-8 w-8 text-emerald-500" /></div></CardContent></Card>
            </div>

            {/* Filters */}
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <CardTitle className="flex items-center gap-2"><Filter className="h-4 w-4" /> Filter & Search</CardTitle>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative w-56">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input placeholder="Search name, phone, email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="pool-search" />
                            </div>
                            <Select value={teamFilter} onValueChange={setTeamFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="team-filter"><SelectValue placeholder="All Teams" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Teams</SelectItem>
                                    {teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={agentFilter} onValueChange={setAgentFilter}>
                                <SelectTrigger className="w-[200px]" data-testid="agent-filter"><SelectValue placeholder="All Agents" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Agents</SelectItem>
                                    {historyAgents
                                        .filter(a => teamFilter === 'all' || (a.team_name || '') === teamFilter)
                                        .map(a => (
                                            <SelectItem key={a.id} value={a.id}>
                                                {a.name}{a.team_name ? ` — ${a.team_name}` : ''}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {canManage && <TableHead className="w-10"><Checkbox checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all" /></TableHead>}
                                    <TableHead>Lead</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Stage</TableHead>
                                    <TableHead>Times Assigned</TableHead>
                                    <TableHead>Last Rejected By</TableHead>
                                    <TableHead>Added</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredLeads.length === 0 ? (
                                    <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center text-muted-foreground py-8">No leads in pool matching filters</TableCell></TableRow>
                                ) : filteredLeads.map(lead => {
                                    const timesAssigned = lead.times_assigned || lead.assignment_history_count || 0;
                                    const hasHistory = (lead.assignment_history_count || 0) > 0 || !!lead.last_rejected_agent_name;
                                    return (
                                        <TableRow key={lead.id} className={selectedLeads.has(lead.id) ? 'bg-primary/5' : ''}>
                                            {canManage && <TableCell><Checkbox checked={selectedLeads.has(lead.id)} onCheckedChange={() => toggleSelectLead(lead.id)} /></TableCell>}
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{lead.full_name}</p>
                                                    {lead.country && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.country}</p>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-0.5">
                                                    <p className="text-sm flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</p>
                                                    {lead.email && <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</p>}
                                                </div>
                                            </TableCell>
                                            <TableCell><Badge variant="outline">{lead.lead_source || 'Direct'}</Badge></TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Badge variant={lead.stage === 'rejected' ? 'destructive' : lead.stage === 'not_interested' ? 'secondary' : 'outline'} className="text-xs capitalize">
                                                        {lead.stage?.replace(/_/g, ' ')}
                                                    </Badge>
                                                    {lead.cross_team_eligible && (
                                                        <Badge className="bg-orange-500/10 text-orange-600 text-[10px] px-1.5">Cross-Team</Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Badge variant={timesAssigned > 2 ? 'destructive' : timesAssigned > 1 ? 'warning' : 'secondary'} className="font-mono">
                                                        {lead.rejection_count || timesAssigned}x rejected
                                                    </Badge>
                                                    {hasHistory && (
                                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => {
                                                            setSelectedLead(lead);
                                                            setHistoryDetail({ loading: true, items: [] });
                                                            setShowHistoryModal(true);
                                                            try {
                                                                const r = await apiClient.get(`/leads/${lead.id}/assignment-history`);
                                                                setHistoryDetail({ loading: false, items: r.data?.assignment_history || [] });
                                                            } catch {
                                                                setHistoryDetail({ loading: false, items: [] });
                                                            }
                                                        }} title="View history">
                                                            <History className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {lead.last_rejected_agent_name ? (
                                                    <div className="text-xs" data-testid={`last-rejected-${lead.id}`}>
                                                        <p className="font-medium text-red-500">{lead.last_rejected_agent_name}</p>
                                                        <p className="text-muted-foreground">{lead.last_rejected_team_name || '—'}</p>
                                                    </div>
                                                ) : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell><span className="text-xs text-muted-foreground">{formatDate(lead.created_at)}</span></TableCell>
                                            <TableCell className="text-right">
                                                {canManage && (
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button variant="ghost" size="sm" onClick={() => handleAssign(lead.id, null)} title="Round Robin" data-testid={`rr-assign-${lead.id}`}>
                                                            <Shuffle className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLead(lead); setSelectedAgent(''); setShowAssignModal(true); }} title="Manual Assign">
                                                            <UserPlus className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Manual Assign Modal */}
            <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assign Lead</DialogTitle>
                        <DialogDescription>Assign {selectedLead?.full_name} to a sales agent</DialogDescription>
                    </DialogHeader>
                    {(selectedLead?.last_rejected_agent_name) && (
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-xs font-medium text-amber-500 mb-1">Last rejection on file</p>
                            <p className="text-xs text-muted-foreground">{selectedLead.last_rejected_agent_name} ({selectedLead.last_rejected_team_name || 'No team'}) — {selectedLead.last_rejected_reason || 'rejected'} — {formatDate(selectedLead.last_rejected_at)}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Select Agent</label>
                        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                            <SelectTrigger data-testid="assign-agent-select"><SelectValue placeholder="Choose an agent" /></SelectTrigger>
                            <SelectContent>
                                {agents.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.full_name} — {roleLabel(a.role)} {a.team_name ? `(${a.team_name})` : ''} {a.region ? `— ${a.region}` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                        <Button onClick={() => handleAssign(selectedLead?.id, selectedAgent)} disabled={!selectedAgent || assigning}>{assigning ? 'Assigning...' : 'Assign Lead'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Assign Modal */}
            <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bulk Assign {selectedLeads.size} Leads</DialogTitle>
                        <DialogDescription>All selected leads will be assigned to one agent</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Select Agent</label>
                        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                            <SelectTrigger data-testid="bulk-assign-agent"><SelectValue placeholder="Choose an agent" /></SelectTrigger>
                            <SelectContent>
                                {agents.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.full_name} — {roleLabel(a.role)} {a.team_name ? `(${a.team_name})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkAssignModal(false)}>Cancel</Button>
                        <Button onClick={handleBulkAssign} disabled={!selectedAgent || assigning}>{assigning ? 'Assigning...' : `Assign ${selectedLeads.size} Leads`}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assignment History Modal */}
            <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Assignment History — {selectedLead?.full_name}</DialogTitle>
                        <DialogDescription>Complete history of all assignments and rejections</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                        {historyDetail.loading ? (
                            <div className="flex items-center justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>
                        ) : historyDetail.items.length === 0 ? (
                            <p className="text-center text-muted-foreground py-4">No assignment history</p>
                        ) : (
                            [...historyDetail.items].reverse().map((h, i) => (
                                <div key={i} className={`p-3 rounded-lg border ${h.action === 'rejected' || h.action === 'not_interested' ? 'border-red-500/30 bg-red-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium">{h.agent_name}</p>
                                            <p className="text-xs text-muted-foreground">{h.team_name || 'No team'}</p>
                                        </div>
                                        <Badge variant={h.action === 'rejected' || h.action === 'not_interested' ? 'destructive' : 'secondary'} className="text-xs capitalize">
                                            {h.action?.replace(/_/g, ' ')}
                                        </Badge>
                                    </div>
                                    {h.reason && <p className="text-xs text-muted-foreground mt-1">Reason: {h.reason}</p>}
                                    {h.assigned_by && <p className="text-xs text-muted-foreground mt-0.5">Assigned by: {h.assigned_by}</p>}
                                    <p className="text-[10px] text-muted-foreground mt-1">{formatDate(h.date)}</p>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LeadsPoolPage;
